/**
 * Overview page — period-aware narrative dashboard (PR 2 of dashboard
 * standards).
 *
 * Story top-to-bottom:
 *   Row 1 — Period selector + "Data as of …" notice
 *   Row 2 — 6 hero KPIs (active fleet, spend, weighted R/L, total
 *           litres, open critical, fraud exposure %)
 *   Row 3 — Spend trend bar chart with VehicleDimensionSelect
 *   Row 4 — Fuel efficiency trend (left, 7/12) + Fraud exposure
 *           summary card linking to the Fraud Hub (right, 5/12)
 *   Row 5 — Top 10 fleet assets table with metric selector
 *
 * The previous "Active fleet — last 24 months" line chart and the
 * hardcoded MTD-only KPI tiles have been removed. The Active Fleet KPI
 * tile in Row 2 already conveys headcount; the 24-month trend was not
 * an executive-relevant signal.
 *
 * `useOverviewKpis` is the anchor query: it returns `data_anchor` (the
 * warehouse `max(transaction_time)`) which we surface in the header
 * "Data as of …" notice. The other three hooks fire in parallel against
 * the same period and are independent of the anchor (the backend
 * resolves it on each request); we don't gate them on `useOverviewKpis`
 * because that would serialise round trips for no benefit — the KPIs
 * query is the fastest of the four and lands first anyway.
 */

import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/fleetguardian';
import DetectiveChips from '@/components/ai/DetectiveChips';
import { TimePeriodSelector } from '@/components/shared/TimePeriodSelector';
import { parsePeriod } from '@/lib/timePeriod';
import { HeroKpiTiles } from '@/components/overview/HeroKpiTiles';
import { SpendTrendChart } from '@/components/overview/SpendTrendChart';
import { EfficiencyTrendChart } from '@/components/overview/EfficiencyTrendChart';
import { FraudExposureCard } from '@/components/overview/FraudExposureCard';
import { TopAssetsTable } from '@/components/overview/TopAssetsTable';
import {
  useOverviewKpis,
  useOverviewSpendTrend,
  useOverviewEfficiencyTrend,
} from '@/api/hooks/useOverview';
import type { VehicleDim } from '@/components/shared/VehicleDimensionSelect';

const DATE_FMT = new Intl.DateTimeFormat('en-ZA', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

export default function Overview() {
  const [searchParams, setSearchParams] = useSearchParams();
  const period = parsePeriod(searchParams.get('period'), 'Q');
  const dim = (searchParams.get('dim') ?? 'all') as VehicleDim;
  // R/litre averaging: weighted (Σspend/Σlitres) by default; ?weighted=false
  // switches the tile to the simple mean of per-transaction unit prices.
  const weighted = searchParams.get('weighted') !== 'false';

  const kpis = useOverviewKpis(period, weighted);
  const spendTrend = useOverviewSpendTrend(period, dim);
  const efficiency = useOverviewEfficiencyTrend(period);

  const setWeighted = (w: boolean) => {
    const next = new URLSearchParams(searchParams);
    next.set('weighted', String(w));
    setSearchParams(next, { replace: false });
  };

  const anchorLabel = kpis.data
    ? DATE_FMT.format(new Date(kpis.data.data_anchor))
    : null;
  const lagDays = kpis.data?.warehouse_lag_days ?? null;

  return (
    <PageContainer>
      {/* Page header */}
      <PageHeader
        title="Overview"
        subtitle="Is the fleet healthy and is the money being spent responsibly?"
      />
      <DetectiveChips module="overview" />

      {/* Row 1: period selector + as-of notice */}
      <div className="flex flex-col items-start justify-between gap-2 md:flex-row md:items-center">
        <div className="flex flex-wrap items-center gap-2">
          <TimePeriodSelector defaultPeriod="Q" />
          <div
            className="inline-flex items-center gap-1 rounded-md border bg-background p-1"
            role="group"
            aria-label="R/litre averaging"
          >
            <Button
              size="sm"
              variant={weighted ? 'default' : 'outline'}
              onClick={() => setWeighted(true)}
              aria-pressed={weighted}
            >
              Weighted
            </Button>
            <Button
              size="sm"
              variant={!weighted ? 'default' : 'outline'}
              onClick={() => setWeighted(false)}
              aria-pressed={!weighted}
            >
              Simple
            </Button>
          </div>
        </div>
        <div className="text-xs text-muted-foreground">
          {anchorLabel ? (
            <>
              Data as of <span className="font-medium text-foreground">{anchorLabel}</span>
              {lagDays != null && lagDays > 0 && (
                <> · Warehouse lag: {lagDays} day{lagDays === 1 ? '' : 's'}</>
              )}
              {kpis.data?.period_label && (
                <> · {kpis.data.period_label}</>
              )}
            </>
          ) : (
            <span>Loading anchor…</span>
          )}
        </div>
      </div>

      {/* KPI / hook errors surface as a single inline banner */}
      {kpis.isError && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-3 text-sm text-red-800">
            Could not load overview KPIs: {(kpis.error as Error).message}
          </CardContent>
        </Card>
      )}

      {/* Row 2: 6 hero KPIs */}
      <HeroKpiTiles data={kpis.data} isLoading={kpis.isLoading} weighted={weighted} />

      {/* Row 3: spend trend (full width) */}
      <SpendTrendChart
        data={spendTrend.data}
        isLoading={spendTrend.isLoading}
        error={(spendTrend.error as Error | null) ?? null}
      />

      {/* Row 4: efficiency trend (left 7) + fraud exposure (right 5) */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <EfficiencyTrendChart
            data={efficiency.data}
            isLoading={efficiency.isLoading}
            error={(efficiency.error as Error | null) ?? null}
          />
        </div>
        <div className="lg:col-span-5">
          <FraudExposureCard />
        </div>
      </div>

      {/* Row 5: top assets table */}
      <TopAssetsTable period={period} />
    </PageContainer>
  );
}
